import { useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";

import {
  documentQueryKeys,
  useCollectionStructure,
  useDocumentPage,
  useMoveDocument,
} from "../../api/documents";
import { collectionPath, useCollectionAbilities, useCollections } from "../../api/queries";
import type { Collection, NavigationNode } from "../../api/types";
import { granted } from "../../auth/AbilityGate";
import { insertNode, removeNode } from "../../documents/tree";
import { cx } from "../../lib/cx";
import { Menu, type MenuItem } from "../../ui/Menu";
import { PlusIcon } from "../../ui/icons";
import { useToast } from "../../ui/toast-context";
import { DocumentTree, nodeLabel } from "./DocumentTree";
import { moveInputFor, type SidebarDrag, type SidebarDropTarget } from "./dnd";

export interface DocumentRowActions {
  onNewChild: (collectionId: string, parentDocumentId: string) => void;
  onMove: (documentId: string, title: string, collectionId: string) => void;
  onArchive: (documentId: string, title: string) => void;
  onDelete: (documentId: string, title: string) => void;
  onDuplicate: (documentId: string, title: string) => void;
  onStar: (documentId: string) => void;
  onUnstar: (documentId: string) => void;
  isStarred: (documentId: string) => boolean;
}

interface CollectionBranchProps extends DocumentRowActions {
  collection: Collection;
  abilities: Record<string, boolean>;
  expanded: ReadonlySet<string>;
  onToggle: (id: string) => void;
  drag: SidebarDrag | null;
  dropTarget: SidebarDropTarget | null;
  onDragStart: (drag: SidebarDrag) => void;
  onDragEnd: () => void;
  onDragOverTarget: (target: SidebarDropTarget) => void;
  onDrop: (target: SidebarDropTarget) => void;
  onNewDocument: (collectionId: string) => void;
}

function CollectionBranch(props: CollectionBranchProps): ReactNode {
  const { collection, abilities, expanded, onToggle } = props;
  const isOpen = expanded.has(collection.id);
  // Lazy: a collection's tree — and the policies for the documents in it — are
  // only fetched once somebody opens it.
  const structure = useCollectionStructure(collection.id, isOpen);
  const documents = useDocumentPage(
    "documents.list",
    { collectionId: collection.id, limit: 100 },
    isOpen,
  );
  const documentAbilities = documents.data?.abilities ?? {};

  const rowActions = (node: NavigationNode): MenuItem[] => {
    const can = documentAbilities[node.id] ?? {};
    const items: MenuItem[] = [];
    if (granted(can, "createChildDocument")) {
      items.push({
        label: "New nested document",
        onSelect: () => {
          props.onNewChild(collection.id, node.id);
        },
      });
    }
    items.push(
      props.isStarred(node.id)
        ? {
            label: "Unstar",
            onSelect: () => {
              props.onUnstar(node.id);
            },
          }
        : {
            label: "Star",
            onSelect: () => {
              props.onStar(node.id);
            },
          },
    );
    if (granted(can, "move")) {
      items.push({
        label: "Move…",
        onSelect: () => {
          props.onMove(node.id, nodeLabel(node), collection.id);
        },
      });
    }
    if (granted(can, "duplicate")) {
      items.push({
        label: "Duplicate",
        onSelect: () => {
          props.onDuplicate(node.id, nodeLabel(node));
        },
      });
    }
    if (granted(can, "archive")) {
      items.push({
        label: "Archive",
        onSelect: () => {
          props.onArchive(node.id, nodeLabel(node));
        },
      });
    }
    if (granted(can, "delete")) {
      items.push({
        label: "Delete",
        destructive: true,
        onSelect: () => {
          props.onDelete(node.id, nodeLabel(node));
        },
      });
    }
    return items;
  };

  const collectionMenu: MenuItem[] = [];
  if (granted(abilities, "createDocument")) {
    collectionMenu.push({
      label: "New document",
      onSelect: () => {
        props.onNewDocument(collection.id);
      },
    });
  }

  return (
    <li>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label={`${isOpen ? "Collapse" : "Expand"} ${collection.name}`}
          aria-expanded={isOpen}
          onClick={() => {
            onToggle(collection.id);
          }}
          className="w-4 shrink-0 text-xs text-muted"
        >
          {isOpen ? "▾" : "▸"}
        </button>
        <NavLink
          to={collectionPath(collection)}
          className={({ isActive }) =>
            cx(
              "flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-1 text-sm",
              isActive
                ? "bg-hover font-medium text-ink"
                : "text-muted hover:bg-hover hover:text-ink",
            )
          }
        >
          <span aria-hidden="true" className="w-4 shrink-0 text-center">
            {collection.icon ?? "📁"}
          </span>
          <span className="truncate">{collection.name}</span>
        </NavLink>
        {collectionMenu.length > 0 && (
          <Menu
            buttonLabel={`Actions for ${collection.name}`}
            menuLabel="Collection actions"
            items={collectionMenu}
            trigger="⋯"
          />
        )}
      </div>

      {isOpen && (
        <div className="pl-4">
          {structure.isPending ? (
            <p className="px-2 py-1 text-xs text-muted">Loading documents…</p>
          ) : structure.isError ? (
            <p className="px-2 py-1 text-xs text-danger">Documents could not be loaded.</p>
          ) : (
            <DocumentTree
              collectionId={collection.id}
              nodes={structure.data ?? []}
              parentDocumentId={null}
              containerLabel={collection.name}
              depth={0}
              expanded={expanded}
              onToggle={onToggle}
              rowActions={rowActions}
              drag={props.drag}
              dropTarget={props.dropTarget}
              onDragStart={props.onDragStart}
              onDragEnd={props.onDragEnd}
              onDragOverTarget={props.onDragOverTarget}
              onDrop={props.onDrop}
            />
          )}
        </div>
      )}
    </li>
  );
}

export interface CollectionsSectionProps extends DocumentRowActions {
  onNewCollection: () => void;
  onNewDocument: (collectionId: string) => void;
  expanded: ReadonlySet<string>;
  onToggle: (id: string) => void;
  canCreateCollection: boolean;
  enabled: boolean;
}

/**
 * The collections tree, and the only place in the client that writes
 * `documents.move`. The write is optimistic against the cached structures of
 * both the source and the destination collection and rolls both back together,
 * because a half-applied move reads as a lost document.
 */
export function CollectionsSection(props: CollectionsSectionProps): ReactNode {
  const collections = useCollections(props.enabled);
  const abilities = useCollectionAbilities(props.enabled);
  const client = useQueryClient();
  const move = useMoveDocument();
  const toast = useToast();
  const [drag, setDrag] = useState<SidebarDrag | null>(null);
  const [dropTarget, setDropTarget] = useState<SidebarDropTarget | null>(null);

  const onDrop = (target: SidebarDropTarget): void => {
    const current = drag;
    setDrag(null);
    setDropTarget(null);
    if (current === null) return;
    const input = moveInputFor(current, target);
    if (input === null) return;

    const sourceKey = documentQueryKeys.structure(current.collectionId);
    const destinationKey = documentQueryKeys.structure(target.collectionId);
    const previousSource = client.getQueryData<NavigationNode[]>(sourceKey);
    const previousDestination = client.getQueryData<NavigationNode[]>(destinationKey);

    const detached = removeNode(previousSource ?? [], current.documentId);
    const moved = detached.removed;
    if (moved !== null) {
      client.setQueryData(sourceKey, detached.nodes);
      const base =
        current.collectionId === target.collectionId ? detached.nodes : (previousDestination ?? []);
      const parentId = input.parentDocumentId ?? null;
      client.setQueryData(destinationKey, insertNode(base, moved, parentId, input.index ?? 0));
    }

    move.mutate(input, {
      onError: (error) => {
        if (previousSource !== undefined) client.setQueryData(sourceKey, previousSource);
        if (previousDestination !== undefined) {
          client.setQueryData(destinationKey, previousDestination);
        }
        toast.show(error.message, "error");
      },
    });
  };

  return (
    <section aria-labelledby="sidebar-collections">
      <div className="mb-1 flex items-center justify-between px-2">
        <h2
          id="sidebar-collections"
          className="text-xs font-semibold tracking-wide text-muted uppercase"
        >
          Collections
        </h2>
        {props.canCreateCollection && (
          <button
            type="button"
            onClick={props.onNewCollection}
            aria-label="New collection"
            title="New collection"
            className="rounded p-1 text-muted hover:bg-hover hover:text-ink"
          >
            <PlusIcon />
          </button>
        )}
      </div>

      {collections.isPending ? (
        <p className="px-2 py-1 text-sm text-muted">Loading collections…</p>
      ) : collections.isError ? (
        <p className="px-2 py-1 text-sm text-danger">Collections could not be loaded.</p>
      ) : (collections.data ?? []).length === 0 ? (
        <p className="px-2 py-1 text-sm text-muted">No collections yet.</p>
      ) : (
        <ul className="flex flex-col gap-0.5">
          {(collections.data ?? []).map((collection) => (
            <CollectionBranch
              key={collection.id}
              collection={collection}
              abilities={abilities.data?.[collection.id] ?? {}}
              expanded={props.expanded}
              onToggle={props.onToggle}
              drag={drag}
              dropTarget={dropTarget}
              onDragStart={setDrag}
              onDragEnd={() => {
                setDrag(null);
                setDropTarget(null);
              }}
              onDragOverTarget={setDropTarget}
              onDrop={onDrop}
              onNewDocument={props.onNewDocument}
              onNewChild={props.onNewChild}
              onMove={props.onMove}
              onArchive={props.onArchive}
              onDelete={props.onDelete}
              onDuplicate={props.onDuplicate}
              onStar={props.onStar}
              onUnstar={props.onUnstar}
              isStarred={props.isStarred}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
